import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './StatsList.css';
import * as st from '../core/st';
import { MAGING_KIND } from '../core/itemMaging';
import Tooltip from './utils/Tooltip';

const EMPTY_LABEL = '';
const MULTI_COLUMN_CLASS = 'multi-column';
const COMPACT_CONTAINER_CLASS = 'stats-list-container--compact';
const LABEL_MODE_LABEL = 'label';
const LABEL_MODE_ALIAS = 'alias';
/** Prefix for a ceiling row (`isCap`), not a bonus — "max. 2 PM". */
/** Ends a line a transcendence rune put on the item (magedStatRows' `trans`). */
const TRANS_SUFFIX = '(Trans.)';
const CAP_PREFIX = { fr: 'max.', en: 'max.', de: 'max.', es: 'máx.', pt: 'máx.' };
const TXT_EDIT_RANGE = { fr: 'Forgemagie', en: 'Smithmagic', de: 'Schmiedemagie', es: 'Forjamagia', pt: 'Forjamagia' };

/**
 * A value the reader can forge: a button until clicked, then a number input
 * that commits on Enter or blur and gives up on Escape. `stat.range` bounds
 * it; the owner clamps anyway, so a typed value past it lands on the edge.
 */
const EditableStatValue = ({ stat, className, lang, onChange, children }) => {
    const [draft, setDraft] = useState(null);
    const range = stat.range;
    if (draft === null) {
        const hint = range
            ? `${TXT_EDIT_RANGE[lang] || TXT_EDIT_RANGE.en} : ${range.min} – ${range.max}`
            : null;
        return (
            <Tooltip content={hint}>
                <button
                    type="button"
                    className={`${className} stats-list-value--editable`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setDraft(String(stat.value));
                    }}
                >
                    {children}
                </button>
            </Tooltip>
        );
    }
    const commit = () => {
        const n = Number(draft);
        if (draft !== '' && Number.isFinite(n) && n !== stat.value) onChange(stat, n);
        setDraft(null);
    };
    return (
        <input
            type="number"
            className="stats-list-value-input"
            autoFocus
            onFocus={(e) => e.target.select()}
            value={draft}
            min={range?.min}
            max={range?.max}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                else if (e.key === 'Escape') {
                    e.stopPropagation();
                    setDraft(null);
                }
            }}
        />
    );
};

/**
 * StatsList component for displaying a list of stats with icon, value, and alias/label
 * 
 * @param {Array} stats - Array of stat objects with icon, value, and id properties
 * @param {string} className - Additional CSS class for styling
 * @param {Function} onStatClick - Optional click handler for stat rows
 * @param {boolean} showIcon - Whether to show icons (default: true)
 * @param {string} lang - Language code ('fr' or 'en'), used to select alias
 * @param {Function} onValueChange - Optional `(stat, value)`: makes every row
 *   carrying a `range` editable in place (item forgemagie)
 *
 * A row's value and label share one colour, as in the game: green, red when
 * negative, and `maging` ('over' | 'exo') tints it; exo rows lead the list.
 */
const StatsList = ({
    stats = [],
    className = '',
    onStatClick = null,
    showIcon = true,
    lang = 'fr',
    compact = false,
    compactMultiColumn = true,
    showCompactLabels = false,
    showLabelsInMultiColumn = false,
    labelMode = LABEL_MODE_LABEL,
    onValueChange = null,
}) => {
    if (!stats || stats.length === 0) {
        return null;
    }

    const handleRowClick = (stat, index) => {
        if (onStatClick) {
            onStatClick(stat, index);
        }
    };

    const isMultiColumn = compact
        ? compactMultiColumn
        : className.includes(MULTI_COLUMN_CLASS);

    const getFullLabel = (stat) => {
        if (stat.label) return stat.label;
        const statInfo = st.STATS.find(s => s.id === stat.id);
        if (!statInfo) return `Stat ${stat.id}`;
        return statInfo.label[lang] || statInfo.label.fr || statInfo.label.en;
    };

    const getAliasLabel = (stat) => {
        const statInfo = st.STATS.find(s => s.id === stat.id);
        if (!statInfo) return getFullLabel(stat);
        const alias = statInfo.alias?.[lang] || statInfo.alias?.fr || statInfo.alias?.en;
        return alias || getFullLabel(stat);
    };

    const getDisplayLabel = (stat) => {
        if (labelMode === LABEL_MODE_ALIAS) return getAliasLabel(stat);
        return getFullLabel(stat);
    };

    const getLabel = (stat, index) => {
        // Keep compact labels optional to preserve existing compact layouts.
        if (compact && !showCompactLabels) return EMPTY_LABEL;
        if (isMultiColumn && !showLabelsInMultiColumn) return EMPTY_LABEL;
        return st.labelBesideValue(getDisplayLabel(stat));
    };

    // The unit rides on the value ("7%"), whether or not a label follows.
    const getDisplayValue = (stat) => {
        const unit = st.STATS.find(s => String(s.id) === String(stat.id))?.unit || '';
        const value = `${stat.value}${unit}`;
        return stat.isCap ? `${CAP_PREFIX[lang] || CAP_PREFIX.en} ${value}` : value;
    };

    // Filter out stats not in STATS list, then sort by their order field from st.STATS
    const filteredStats = stats.filter(stat => 
        st.STATS.some(s => String(s.id) === String(stat.id))
    );
    
    const sortedStats = [...filteredStats].sort((a, b) => {
        const exoA = a.maging === MAGING_KIND.EXO ? 0 : 1;
        const exoB = b.maging === MAGING_KIND.EXO ? 0 : 1;
        if (exoA !== exoB) return exoA - exoB;
        const statA = st.STATS.find(s => String(s.id) === String(a.id));
        const statB = st.STATS.find(s => String(s.id) === String(b.id));

        const orderA = statA ? statA.order : Number.MAX_SAFE_INTEGER;
        const orderB = statB ? statB.order : Number.MAX_SAFE_INTEGER;

        return orderA - orderB;
    });

    const compactClassName = compact ? COMPACT_CONTAINER_CLASS : EMPTY_LABEL;
    const multiColumnClassName = isMultiColumn ? MULTI_COLUMN_CLASS : EMPTY_LABEL;
    const containerClassName = `stats-list-container ${className} ${compactClassName} ${multiColumnClassName}`;

    return (
        <div className={containerClassName}>
            {sortedStats.map((stat, index) => {
                const toneClassName = `${stat.isCap ? 'cap' : ''} ${stat.value < 0 ? 'negative' : ''} ${stat.maging || ''}`;
                return (
                <div
                    key={index}
                    className={`stats-list-row ${onStatClick ? 'stats-list-row-clickable' : ''}`}
                    onClick={() => handleRowClick(stat, index)}
                >
                    {showIcon && stat.icon && (
                        <img
                            src={stat.icon}
                            alt={getFullLabel(stat)}
                            className="stats-list-icon"
                        />
                    )}
                    <div className={`stats-value-label row ${toneClassName}`}>
                        {onValueChange && stat.range ? (
                            <EditableStatValue
                                stat={stat}
                                className="stats-list-value"
                                lang={lang}
                                onChange={onValueChange}
                            >
                                {getDisplayValue(stat)}
                            </EditableStatValue>
                        ) : (
                            <span className="stats-list-value">
                                {getDisplayValue(stat)}
                            </span>
                        )}
                        <span className="stats-list-label">
                            {getLabel(stat, index)}
                            {stat.trans ? <> <span className="stats-list-trans">{TRANS_SUFFIX}</span></> : null}
                        </span>
                    </div>
                </div>
                );
            })}
        </div>
    );
};

StatsList.propTypes = {
    stats: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        icon: PropTypes.string,
        value: PropTypes.number.isRequired,
        label: PropTypes.string,
        isCap: PropTypes.bool,
        maging: PropTypes.oneOf([MAGING_KIND.OVER, MAGING_KIND.EXO, null]),
        trans: PropTypes.bool,
        range: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
    })),
    className: PropTypes.string,
    onStatClick: PropTypes.func,
    showIcon: PropTypes.bool,
    lang: PropTypes.string,
    compact: PropTypes.bool,
    compactMultiColumn: PropTypes.bool,
    showCompactLabels: PropTypes.bool,
    showLabelsInMultiColumn: PropTypes.bool,
    labelMode: PropTypes.oneOf([LABEL_MODE_LABEL, LABEL_MODE_ALIAS]),
    onValueChange: PropTypes.func,
};

export default StatsList; 